import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { askInventoryQuestion } from '../api/aiAssistantAPI';
import InventoryList from '../components/InventoryList';
import DOMPurify from 'dompurify';
import '../styles/aiAssistant.css';

const contextTypeIcons = {
  inventory_items: "inventory_2", // Material icon for inventory
  stock_items: "warehouse", // Material icon for stock
  history: "history", // Material icon for history
  none: "chat", // Default chat icon
};

const getContextIcon = (contextType) => {
  return contextTypeIcons[contextType] || contextTypeIcons.none;
};

const Message = ({ type, content, contextType }) => (
  <div
    className={`d-flex ${type === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`}
  >
    <div
      className={`p-3 rounded-4 ${
        type === 'user' 
          ? 'bg-primary text-white rounded-bottom-end-0' 
          : 'message-content-container bg-body-tertiary rounded-bottom-start-0'
      }`}
      style={{ maxWidth: '70%' }}
    >
      {type === 'assistant' && (
        <div className="d-flex align-items-center gap-2 mb-2 text-muted">
          <i className="material-icons" style={{ fontSize: '20px' }}>
            {getContextIcon(contextType)}
          </i>
          <small className="text-capitalize">
            {contextType?.replace(/_/g, ' ') || 'General Response'}
          </small>
        </div>
      )}
      {type === 'assistant' 
        ? <div className="message-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} /> 
        : content
      }
    </div>
  </div>
);

const AIAssistantScreen = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inventoryItemIds, setInventoryItemIds] = useState([]);
  const [response, setResponse] = useState(null);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    setInventoryItemIds([]);

    // Add user message to chat
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);

    try {
      setLoading(true);
      const response = await askInventoryQuestion({
        question: userMessage
      });

      setResponse(response);
      
      console.log('AI Response:', response);

      // Add the text response with context type
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: response.answer,
        contextType: response.detected_context_type
      }]);

      // Get inventory item IDs directly from the response
      if (response.inventory_item_ids && response.inventory_item_ids.length > 0) {
        setInventoryItemIds(response.inventory_item_ids);
        
        // Add a message indicating we're displaying the items
        setMessages(prev => [...prev, { 
          type: 'assistant', 
          content: `Displaying ${response.inventory_item_ids.length} relevant inventory item${response.inventory_item_ids.length === 1 ? '' : 's'} in the table below.`,
          contextType: response.detected_context_type
        }]);
      }
    } catch (err) {
      console.error('Error in handleSend:', err);
      setError(err.message || 'Failed to get response');
      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: 'Sorry, I encountered an error processing your request.',
        contextType: 'none'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col md={11} lg={10}>
          <h1>Inventory AI Assistant</h1>
          <div className="card shadow theme-card" style={{ 
            height: inventoryItemIds.length > 0 ? 'calc(85vh - 2rem)' : 'calc(100vh - 2rem)',
            maxHeight: '1200px'
          }}>
            {/* Messages Area */}
            <div className="card-body overflow-auto" style={{ 
              height: 'calc(100% - 180px)'
            }}>
              {messages.map((message, index) => (
                <Message key={index} type={message.type} content={message.content} contextType={message.contextType} />
              ))}
              <div ref={messagesEndRef} />
              
              {loading && (
                <div className="d-flex justify-content-center mt-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
              
              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}
            </div>

            {/* Input Area */}
            <div className="card-footer theme-card-footer">
              <Form onSubmit={handleSend}>
                <Form.Control
                  as="textarea"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    // If Enter is pressed without Shift key, submit the form
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault(); // Prevent new line
                      if (input.trim() && !loading) {
                        handleSend(e);
                      }
                    }
                  }}
                  placeholder="Ask a question about inventory..."
                  disabled={loading}
                  rows="3"
                  className="mb-2 theme-input"
                  style={{
                    resize: 'none',
                    borderRadius: '4px'
                  }}
                    autoComplete="off"
                    spellCheck="true"
                />
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Press <kbd>Enter</kbd> to send, <kbd>Shift+Enter</kbd> for a new line
                  </small>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!input.trim() || loading}
                    className="d-flex align-items-center justify-content-center gap-2"
                    style={{
                      height: '38px',
                      width: '100px',
                      minWidth: '100px'
                    }}
                  >
                    <span>Send</span>
                    <i className="material-icons">send</i>
                  </Button>
                </div>
              </Form>
            </div>
          </div>
          
          {/* Add InventoryList below the chat if we have IDs to display */}
          {inventoryItemIds.length > 0 && 
           (response?.detected_context_type === 'inventory_items' || response?.detected_context_type === 'history') && (
            <div className="mt-3">
              {response?.detected_context_type === 'history' ? (
                <>
                  <h3>Referenced Inventory Items:</h3>
                  <div className="alert alert-info">
                    <i className="material-icons align-middle me-2" style={{ fontSize: '20px' }}>info</i>
                    Some historical items might not appear in the table if they have been deleted from the system.
                  </div>
                </>
              ) : (
                <h3>Matching Inventory Items:</h3>
              )}
              <InventoryList inventoryIds={inventoryItemIds} />
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default AIAssistantScreen; 